import { useMemo } from 'react';

export default function ContractorComparisonBars({ contractors = [] }) {
  const metrics = useMemo(() => {
    if (contractors.length === 0) return [];

    return [
      {
        name: 'Safety Rating',
        key: 'safetyRating',
        max: 100,
        unit: '',
        color: 'bg-blue-500',
      },
      {
        name: 'Violations/Worker',
        key: 'normalizedRate',
        max: Math.max(...contractors.map(c => c.normalizedRate || 0)) || 1,
        unit: '/worker',
        color: 'bg-amber-500',
      },
      {
        name: 'Avg Worker Score',
        key: 'avgWorkerScore',
        max: 100,
        unit: '',
        color: 'bg-emerald-500',
      },
    ];
  }, [contractors]);

  if (contractors.length < 2) {
    return null;
  }

  return (
    <div className="space-y-6">
      {metrics.map(metric => (
        <div key={metric.key}>
          <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">{metric.name}</h4>
          <div className="space-y-2">
            {contractors.map(contractor => {
              const value = contractor[metric.key] || 0;
              const pct = (value / metric.max) * 100;

              return (
                <div key={contractor.id}>
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs font-medium text-slate-700">{contractor.name}</span>
                    <span className="text-xs font-bold text-slate-800">
                      {Number.isInteger(value) ? value : value.toFixed(2)}{metric.unit}
                    </span>
                  </div>
                  <div className="relative h-6 bg-slate-100 rounded-lg overflow-hidden">
                    <div
                      className={`h-full ${metric.color} transition-all duration-500 rounded-lg`}
                      style={{ width: `${Math.min(pct, 100)}%` }}
                    />
                    {pct > 20 && (
                      <div className="absolute inset-0 flex items-center px-2">
                        <span className="text-xs font-semibold text-white drop-shadow-sm">
                          {Math.round(pct)}%
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* Risk Distribution Comparison */}
      <div>
        <h4 className="text-xs font-semibold text-slate-600 uppercase tracking-wider mb-3">Risk Level Breakdown</h4>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {contractors.map(contractor => (
            <div key={contractor.id} className="bg-slate-50 rounded-lg p-3">
              <p className="text-xs font-semibold text-slate-700 mb-2">{contractor.name}</p>
              <div className="space-y-1">
                {contractor.severityBreakdown && Object.entries(contractor.severityBreakdown).map(([severity, count]) => {
                  const total = Object.values(contractor.severityBreakdown).reduce((a, b) => a + b, 0) || 1;
                  const pct = (count / total) * 100;

                  return (
                    <div key={severity}>
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-[10px] text-slate-600">{severity}</span>
                        <span className="text-[10px] font-bold text-slate-700">{count}</span>
                      </div>
                      <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                        <div
                          className={`h-full rounded-full transition-all duration-500 ${
                            severity === 'CRITICAL' ? 'bg-red-500' :
                            severity === 'HIGH' ? 'bg-orange-500' :
                            severity === 'MEDIUM' ? 'bg-amber-500' : 'bg-blue-500'
                          }`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
